import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useColorMode, useTheme } from "@chakra-ui/react";

/**
 * Reusable ApexCharts wrapper component with Chakra UI integration
 * Provides automatic dark mode support and consistent theming
 */
const ApexChart = ({
  type = "line",
  series = [],
  options = {},
  height = "350px",
  width = "100%",
  ...rest
}) => {
  const { colorMode } = useColorMode();
  const theme = useTheme();

  // Get Chakra theme colors
  const isDark = colorMode === "dark";
  const textColor = isDark ? "#ffffff" : "#2D3748";
  const gridColor = isDark ? "#56577A" : "#E2E8F0";

  // Default ApexCharts configuration with Chakra UI theming
  const defaultOptions = useMemo(
    () => ({
      chart: {
        background: "transparent",
        foreColor: textColor,
        fontFamily: theme.fonts?.body || "Open Sans, sans-serif",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
          autoSelected: "zoom",
        },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      theme: {
        mode: colorMode,
      },
      grid: {
        borderColor: gridColor,
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      xaxis: {
        labels: {
          style: {
            colors: textColor,
            fontSize: "12px",
          },
        },
        axisBorder: {
          show: true,
          color: gridColor,
        },
        axisTicks: {
          show: true,
          color: gridColor,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: textColor,
            fontSize: "12px",
          },
        },
      },
      tooltip: {
        theme: colorMode,
        style: {
          fontSize: "12px",
        },
        x: {
          show: true,
        },
        y: {
          formatter: undefined,
        },
      },
      legend: {
        labels: {
          colors: textColor,
        },
        fontSize: "14px",
        fontWeight: 500,
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: colorMode,
          type: "vertical",
          shadeIntensity: 0.5,
          gradientToColors: undefined,
          inverseColors: true,
          opacityFrom: 0.7,
          opacityTo: 0.3,
          stops: [0, 90, 100],
        },
      },
      colors: [
        theme.colors?.blue?.[500] || "#4FD1C5",
        theme.colors?.purple?.[500] || "#9F7AEA",
        theme.colors?.green?.[500] || "#48BB78",
        theme.colors?.orange?.[500] || "#ED8936",
        theme.colors?.pink?.[500] || "#ED64A6",
      ],
    }),
    [colorMode, textColor, gridColor, theme]
  );

  // Merge default options with user-provided options
  const mergedOptions = useMemo(() => {
    return {
      ...defaultOptions,
      ...options,
      chart: {
        ...defaultOptions.chart,
        ...(options.chart || {}),
      },
      xaxis: {
        ...defaultOptions.xaxis,
        ...(options.xaxis || {}),
      },
      yaxis: {
        ...defaultOptions.yaxis,
        ...(options.yaxis || {}),
      },
      tooltip: {
        ...defaultOptions.tooltip,
        ...(options.tooltip || {}),
      },
      theme: {
        ...defaultOptions.theme,
        ...(options.theme || {}),
      },
    };
  }, [defaultOptions, options]);

  return (
    <ReactApexChart
      options={mergedOptions}
      series={series}
      type={type}
      height={height}
      width={width}
      {...rest}
    />
  );
};

export default ApexChart;
